import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { fetchLeadRevenueBenchmark } from '@src/api/actions/dashboard/leadRevenue';

const fmtUsd = (v) =>
  v == null
    ? '—'
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

// Ratio bands: predicted/realized ~1.0 means the client is realizing what the
// leads predict. High ratio = under-realizing (coaching or data conversation);
// low = out-performing the model. Status colors reserved for state, per the
// design rules — icon-free chips carry a text label, never color alone.
function ratioChip(row) {
  const r = row.ratio;
  if (r == null) return <Chip size="small" variant="outlined" label="no sales" />;
  let color = 'success';
  let label = `${r.toFixed(2)} on track`;
  if (r > 1.6) {
    color = 'error';
    label = `${r.toFixed(2)} under-realizing`;
  } else if (r > 1.25) {
    color = 'warning';
    label = `${r.toFixed(2)} lagging`;
  } else if (r < 0.6) {
    color = 'info';
    label = `${r.toFixed(2)} outperforming`;
  }
  // Weak backend reporting inflates the ratio no matter how the client sells —
  // call that out instead of letting it read as underperformance.
  if (row.match_coverage < 0.5 && r > 1.25) {
    return (
      <Tooltip title="Low backend match coverage — predicted vs realized is unreliable; fix data reporting before drawing conclusions." arrow>
        <Chip size="small" color="default" variant="outlined" label={`${r.toFixed(2)} low coverage`} />
      </Tooltip>
    );
  }
  return <Chip size="small" color={color} variant="outlined" label={label} />;
}

const COLUMNS = [
  { key: 'client_name', label: 'Client', align: 'left' },
  { key: 'vertical', label: 'Vertical', align: 'left' },
  { key: 'n_leads', label: 'Leads', align: 'right' },
  { key: 'match_coverage', label: 'Backend coverage', align: 'right' },
  { key: 'predicted_revenue', label: 'Predicted $', align: 'right' },
  { key: 'realized_net', label: 'Realized $', align: 'right' },
  { key: 'ratio', label: 'Predicted / realized', align: 'left' },
  { key: 'predicted_close_rate', label: 'Pred. close', align: 'right' },
  { key: 'realized_close_rate', label: 'Actual close', align: 'right' },
  { key: 'avg_deal_predicted', label: 'Pred. deal', align: 'right' },
  { key: 'avg_deal_realized', label: 'Actual deal', align: 'right' },
];

/**
 * Internal (staff) predicted-vs-realized benchmark.
 *
 * The coaching / retention view: "this client's leads predict $X, they're
 * realizing $Y" — decomposed into backend coverage, close-rate gap and
 * deal-size gap so a low ratio is DIAGNOSED (data problem? thin sample? sales
 * execution?) before anyone has that conversation with a client.
 */
export default function LeadRevenueBenchmarkPage() {
  const [windowDays, setWindowDays] = useState(90);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [orderBy, setOrderBy] = useState('predicted_revenue');
  const [orderDir, setOrderDir] = useState('desc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchLeadRevenueBenchmark({ window_days: windowDays })
      .then((resp) => {
        if (!cancelled) setPayload(resp?.data || resp);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the benchmark.');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  const rows = useMemo(() => {
    let out = payload?.rows || [];
    if (filter.trim()) {
      const q = filter.trim().toLowerCase();
      out = out.filter(
        (r) =>
          (r.client_name || '').toLowerCase().includes(q) ||
          (r.vertical || '').toLowerCase().includes(q)
      );
    }
    const dir = orderDir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
  }, [payload, filter, orderBy, orderDir]);

  const handleSort = (key) => {
    if (orderBy === key) {
      setOrderDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(key);
      setOrderDir('desc');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        sx={{ mb: 2 }}
        spacing={1.5}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Predicted vs Realized
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Matured cohorts only{' '}
            {payload?.window
              ? `(leads ${payload.window.from} → ${payload.window.to}, ${payload.maturity_days}d maturity)`
              : ''}
            . Diagnose before you coach: low coverage = data problem, not sales problem.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <TextField
            size="small"
            label="Filter client / vertical"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <TextField
            size="small"
            select
            label="Window"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value={60}>60 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
            <MenuItem value={180}>180 days</MenuItem>
            <MenuItem value={365}>365 days</MenuItem>
          </TextField>
        </Stack>
      </Stack>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {payload?.note && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {payload.note === 'no active model'
            ? 'No trained lead-revenue model is active yet — train one first.'
            : payload.note}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell key={c.key} align={c.align} sortDirection={orderBy === c.key ? orderDir : false}>
                    <TableSortLabel
                      active={orderBy === c.key}
                      direction={orderBy === c.key ? orderDir : 'desc'}
                      onClick={() => handleSort(c.key)}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.client_id}-${r.vertical}`} hover>
                  <TableCell>{r.client_name}</TableCell>
                  <TableCell>{r.vertical}</TableCell>
                  <TableCell align="right">{r.n_leads.toLocaleString()}</TableCell>
                  <TableCell align="right">{fmtPct(r.match_coverage)}</TableCell>
                  <TableCell align="right">{fmtUsd(r.predicted_revenue)}</TableCell>
                  <TableCell align="right">{fmtUsd(r.realized_net)}</TableCell>
                  <TableCell>{ratioChip(r)}</TableCell>
                  <TableCell align="right">{fmtPct(r.predicted_close_rate)}</TableCell>
                  <TableCell align="right">{fmtPct(r.realized_close_rate)}</TableCell>
                  <TableCell align="right">{fmtUsd(r.avg_deal_predicted)}</TableCell>
                  <TableCell align="right">{fmtUsd(r.avg_deal_realized)}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No matured, scored cohorts in this window yet — the rollup
                      needs scored leads that are ≥30 days old.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
